/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
/* eslint-disable camelcase */
// eslint-disable-next-line import/no-extraneous-dependencies
import { omit } from 'lodash';
import {
  SET_REPORT,
  ADD_REPORT,
  EDIT_REPORT,
  DELETE_REPORT,
  SYNC_REPORT_CACHE,
} from './actions';

const removeReportEntriesById = (state, reportId) => {
  if (!reportId) {
    return state;
  }

  return Object.keys(state).reduce((nextState, creationMethod) => {
    const reportsByResource = nextState[creationMethod];
    if (!reportsByResource) {
      return nextState;
    }

    const resourceIdsToRemove = Object.keys(reportsByResource).filter(
      resourceId => reportsByResource[resourceId]?.id === reportId,
    );

    if (!resourceIdsToRemove.length) {
      return nextState;
    }

    return {
      ...nextState,
      [creationMethod]: omit(reportsByResource, resourceIdsToRemove),
    };
  }, state);
};

const upsertReport = (state, report) => {
  if (!report) {
    return state;
  }

  const reportTypeId =
    report.dashboard || report.chart || report.dashboard_id || report.chart_id;

  if (!report.creation_method || !reportTypeId) {
    return state;
  }

  return {
    ...state,
    [report.creation_method]: {
      ...state[report.creation_method],
      [reportTypeId]: report,
    },
  };
};

export default function reportsReducer(state = {}, action) {
  const actionHandlers = {
    [SET_REPORT]() {
      const { report, resourceId, creationMethod, filterField } = action;
      // For now report count should only be one, but we are checking in case
      // functionality changes.
      const reportObject = report.result?.find(
        report => report[filterField] === resourceId,
      );

      if (reportObject) {
        return upsertReport(
          removeReportEntriesById(state, reportObject.id),
          reportObject,
        );
      }
      if (state?.[creationMethod]?.[resourceId]) {
        // remove the empty report from state
        const newState = { ...state };
        delete newState[creationMethod][resourceId];
        return newState;
      }
      return { ...state };
    },

    [ADD_REPORT]() {
      const { result, id } = action.json;
      const report = { ...result, id };
      return upsertReport(removeReportEntriesById(state, report.id), report);
    },

    [EDIT_REPORT]() {
      const report = {
        ...action.json.result,
        id: action.json.id,
      };
      return upsertReport(removeReportEntriesById(state, report.id), report);
    },

    [SYNC_REPORT_CACHE]() {
      return upsertReport(
        removeReportEntriesById(state, action.report?.id),
        action.report,
      );
    },

    [DELETE_REPORT]() {
      const { report } = action;
      return removeReportEntriesById(state, report.id);
    },
  };

  if (action.type in actionHandlers) {
    return actionHandlers[action.type]();
  }
  return state;
}
